export type CsvValue = string | number | boolean | null | undefined;

function escapeCell(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'boolean' ? (value ? 'true' : 'false') : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function buildCsv(headers: string[], rows: CsvValue[][]): string {
  return [headers, ...rows].map((row) => row.map(escapeCell).join(',')).join('\r\n');
}

/**
 * Fetch up to `limit` rows (null = all) of a paginated endpoint. Page 1 is
 * fetched alone to learn the total; the remaining pages run in concurrent
 * batches. Row order matches page order.
 */
export async function fetchPagedRows<T>(
  fetchPage: (page: number, pageSize: number) => Promise<{ rows: T[]; total: number }>,
  options: { limit?: number | null; pageSize: number; concurrency?: number }
): Promise<T[]> {
  const { pageSize, concurrency = 4 } = options;
  const first = await fetchPage(1, pageSize);
  const target = Math.min(options.limit ?? first.total, first.total);
  const rows = [...first.rows];
  const pageCount = Math.ceil(target / pageSize);
  let nextPage = 2;
  while (nextPage <= pageCount && rows.length < target) {
    const batch: Promise<{ rows: T[]; total: number }>[] = [];
    while (batch.length < concurrency && nextPage <= pageCount) {
      batch.push(fetchPage(nextPage, pageSize));
      nextPage += 1;
    }
    const results = await Promise.all(batch);
    results.forEach((result) => rows.push(...result.rows));
    if (results.some((result) => result.rows.length === 0)) break;
  }
  return rows.slice(0, target);
}

/** Build the CSV, wrap it in a UTF-8-BOM blob (so Excel decodes it), and trigger a download. */
export function downloadCsv(filename: string, headers: string[], rows: CsvValue[][]): void {
  const blob = new Blob(['﻿' + buildCsv(headers, rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
